import {
  EDP_RX_ID,
  PHILOSOPHIES,
  DASHBOARD_TYPES,
  KPI_LIBRARY,
  ROLE_SPECS,
  AI_FEATURES,
  BRIEFING_SCHEDULE,
  DEMO_DECISIONS,
  DEMO_ALERTS,
  DEMO_BRIEFING,
  buildRoleExpDashboard,
} from '../../rolexp/framework';
import {
  RoleCockpit, DecisionCenterPanel, AlertCenterWidget,
  ExecutiveBriefingCard, PersonalWorkspaceNav,
} from '../../components/rolexp';
import { Card, KpiCard } from '../../components/ui';

export function RoleExperiencePage() {
  const dashboard = buildRoleExpDashboard();
  const brandOwner = ROLE_SPECS.find((r) => r.role === 'brand_owner')!;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_RX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Role Experience Architecture</h1>
        <p className="mt-1 text-sm text-slate-500">
          Executive dashboards, operational cockpits, role-based workspaces, decision intelligence, and personalized AI assistance.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Supported Roles" value={dashboard.supported_roles.toString()} />
        <KpiCard label="Dashboard Types" value={dashboard.dashboard_types.toString()} />
        <KpiCard label="KPI Categories" value={dashboard.kpi_categories.toString()} />
        <KpiCard label="Personal Sections" value={dashboard.personal_sections.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Role Philosophy</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Role Dashboards</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {DASHBOARD_TYPES.map((d) => (
            <span key={d.id} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300">
              {d.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Supported Roles</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ROLE_SPECS.map((r) => (
            <Card key={r.role} padding="sm">
              <p className="font-medium">{r.label}</p>
              <p className="text-xs text-slate-500">{DASHBOARD_TYPES.find((d) => d.id === r.dashboard)?.label}</p>
              {r.default_home && <p className="mt-1 text-[10px] text-slate-400">{r.default_home}</p>}
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">KPI Library</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {KPI_LIBRARY.map((k) => (
            <span key={k.id} className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
              {k.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Personal Workspace</h2>
        <PersonalWorkspaceNav className="mt-3" />
      </section>

      <section>
        <h2 className="text-lg font-semibold">Live Cockpit Preview</h2>
        <p className="mt-1 text-sm text-slate-500">Brand Owner role — decision center, alerts, and executive briefing.</p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <RoleCockpit role={brandOwner} />
          <ExecutiveBriefingCard greeting={DEMO_BRIEFING.greeting} sections={DEMO_BRIEFING.sections} />
          <DecisionCenterPanel decisions={DEMO_DECISIONS} />
          <AlertCenterWidget alerts={DEMO_ALERTS} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Role AI Features</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {AI_FEATURES.map((f) => (
              <span key={f} className="rounded-lg bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:bg-ai-950 dark:text-ai-300">{f}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Executive Briefing Schedule</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {BRIEFING_SCHEDULE.map((b) => <li key={b}>• {b}</li>)}
          </ul>
        </div>
      </section>

      <Card className="border-brand-200 bg-brand-50/30 dark:border-brand-900 dark:bg-brand-950/20">
        <p className="text-sm font-medium">Developer Implementation</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Registry: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/rolexp/</code> ·
          Briefing: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/copilot/briefing.go</code> ·
          Command Center: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">/command</code>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00006_EXECUTIVE_DASHBOARDS_ROLE_EXPERIENCE.md</code>
        </p>
      </Card>
    </div>
  );
}
